/**
 * The SPA's single door to the network.
 *
 * Everything that leaves this browser leaves through this directory, and an eslint rule
 * bans `fetch(` outside it. That is `urls.ts`'s pattern applied client-side, for the same
 * reason c29 gave for the server: **two readers of one convention are one decision with
 * two answers.** The convention here is the organization header, and getting it wrong is
 * not a bug that shows up as an error — it is a request answered for the wrong tenant, or
 * for none.
 *
 * ## The org header is set in exactly one function
 *
 * `x-packagepage-org` names which organization a request is for, and the API refuses a
 * request without it (`ORG_NOT_SPECIFIED`). {@link orgHeaders} is the only place it is
 * written. Its value comes from `orgs.mine` — the query that decides what the header may
 * be set to — never from a URL, never from an input, and never from anything a page can
 * put there.
 *
 * ## Types come from `packages/shared`, and the boundary is why
 *
 * The obvious arrangement is tRPC's: import `AppRouter` from `apps/api` as a type and get
 * inference across the wire. **That was built and put back.** A type import is still an
 * edge to dependency-cruiser, so `apps/app -> apps/api -> packages/db` trips
 * `spa-has-no-server-packages-reachable`, and a `reachable` rule combines with
 * `path`/`pathNot` only — there is no way to exempt one intermediate. The choice was the
 * typed client or that rule, and the rule won.
 *
 * So every response is parsed with the schema in `packages/shared/src/api/responses.ts`
 * that the op's return type is inferred from. That is **one definition per shape**, which
 * is what R7 requires, and the parse is a runtime check where the import would have been
 * a compile-time assertion — strictly more checkable, in exchange for inference.
 *
 * The residue, named rather than hidden: **procedure paths are strings here** and nothing
 * compile-checks them. A wrong path is a `NOT_FOUND` on the first run.
 *
 * ## Cookies are first-party because Vite proxies, not because the API relaxed
 *
 * `/v1`, `/trpc` and `/api/auth` are same-origin in development (see `vite.config.ts`)
 * and same-origin in production once c39 puts Caddy in front of both. **No CORS
 * middleware is added to the API** — permissive CORS for a dev origin changes the
 * security surface of every route to serve a need a proxy meets for free, and
 * `Access-Control-Allow-Credentials` is the specific fix nobody should reach for when a
 * cookie goes missing here.
 */
import { QueryClient } from "@tanstack/react-query";
import { createTRPCUntypedClient, httpBatchLink, TRPCClientError } from "@trpc/client";
import type { ZodType } from "zod";
import type { ProblemBody } from "@packagepage/shared";

/**
 * The organization the SPA is currently acting in.
 *
 * Module state rather than React state on purpose: the tRPC link is created once and
 * reads this on every request, so it has to be reachable from outside the component tree.
 * It is written by {@link setActiveOrg} alone, which the shell calls once memberships are
 * known.
 *
 * **Not persisted.** `acceptance-tests.md` requires that no flow's state survives only in
 * browser storage, and this is the value that would be most tempting to keep there:
 * restoring it on load would mean a header set from something the server never confirmed.
 * `orgs.mine` is cheap and is the authority.
 */
let activeOrgId: string | null = null;

/**
 * Everything waiting to hear that the active organization changed.
 *
 * **Module state that React reads during render has to be subscribable, or the read is a
 * torn one.** Added at c37b, from a defect the E2E harness found on its first real-browser
 * load of the dashboard: `OrgGate` set this value in an effect and then gated its own
 * render on reading it back, and nothing woke React up. The gate's fallback — a refetch
 * triggered by `invalidateQueries()` — returns byte-identical data on that path, and
 * TanStack Query's structural sharing hands back the **same `data` object**, so no tracked
 * property of the query result changes and the observer never notifies. The re-render that
 * would have shown the dashboard never happened, for every signed-in user, on a production
 * build. Measured, not deduced: `fetchStatus` cycled and `dataUpdatedAt` advanced while the
 * data reference stayed identical, and the render count stopped at two.
 *
 * The transferable half is worth stating where a reader of this file will meet it:
 * **`invalidateQueries()` is not a re-render primitive.** It is a cache instruction, and an
 * invalidation whose refetch returns identical bytes notifies nobody.
 *
 * This does not make anything a second writer. {@link setActiveOrg} is still the only
 * function that assigns `activeOrgId`; subscribing is a reader's capability.
 */
const activeOrgListeners = new Set<() => void>();

export function setActiveOrg(orgId: string | null): void {
  if (activeOrgId === orgId) return;
  activeOrgId = orgId;
  for (const listener of activeOrgListeners) listener();
}

export function getActiveOrg(): string | null {
  return activeOrgId;
}

/**
 * Subscribe to changes in the active organization. Returns the unsubscribe function, which
 * is the shape `useSyncExternalStore` takes.
 */
export function subscribeActiveOrg(listener: () => void): () => void {
  activeOrgListeners.add(listener);
  return () => {
    activeOrgListeners.delete(listener);
  };
}

/**
 * The one place `x-packagepage-org` is written. See the header.
 *
 * Exported from c92, and the export does not widen the rule it enforces — it is what keeps
 * it. `artwork.ts` in this directory has to make a plain `fetch`, because the artwork
 * response **is a file** and tRPC cannot carry bytes; the choice was that call composing
 * this function or spelling the header name itself, and a second spelling of the header is
 * the failure this file exists to prevent. Every caller is still inside `src/api/`, where
 * the eslint ban on `fetch(` stops a fourth one appearing somewhere else.
 */
export function orgHeaders(): Record<string, string> {
  return activeOrgId === null ? {} : { "x-packagepage-org": activeOrgId };
}

/**
 * The transport. Untyped by construction — see the header on why the router type is not
 * imported — and never called directly outside this file: {@link query} and
 * {@link mutate} are the door, and they are what attach a schema.
 */
const transport = createTRPCUntypedClient({
  links: [
    httpBatchLink({
      // A relative URL, and that is the whole cookie story.
      //
      // The session is a cookie (§6: revocation has to be instant and there is exactly
      // one API origin), and `fetch`'s default `credentials: "same-origin"` sends it
      // without being asked — because the proxy makes `/trpc` same-origin in development
      // and c39's Caddy makes it same-origin in production. **No `credentials: "include"`
      // and no CORS**: `include` would be the setting somebody adds when the cookie goes
      // missing, and it only helps once the origins have already been allowed to differ,
      // which is the change this arrangement exists to avoid.
      url: "/trpc",
      headers: orgHeaders,
    }),
  ],
});

/**
 * Thrown when the server answered successfully with a shape the schema does not describe.
 *
 * A distinct class from a refusal, because it is a distinct event: a `Refusal` is the
 * server declining and is something to show the user, and this is the client and the
 * server disagreeing about what a response *is* — a defect, and one no screen can act on.
 * Keeping them apart is what stops a deploy skew being rendered as though the user had
 * done something wrong.
 */
export class ResponseShapeError extends Error {
  override readonly name = "ResponseShapeError";
}

/**
 * Call a query procedure and validate its answer.
 *
 * The schema is the same one the op's return type is inferred from, so this is one
 * definition checked twice rather than two definitions hoped to agree. A parse failure is
 * loud on purpose: silently accepting a response that does not match would put the SPA in
 * the state where it renders a field that is not there.
 */
export async function query<T>(
  path: string,
  schema: ZodType<T>,
  /**
   * The procedure's input, already shaped by the shared request schema.
   *
   * Shaped by the **caller**, not here: `productListQuerySchema` applies defaults
   * (`limit`, `sort`, `order`) and those defaults belong to one definition, so the
   * call site parses and this transports. Passing a raw object would send a query
   * the server then defaults differently from the one the screen believes it asked.
   */
  input?: unknown,
): Promise<T> {
  const raw: unknown = await transport.query(path, input);
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new ResponseShapeError(
      `${path} answered a shape this client does not recognise: ` +
        `${parsed.error.issues[0]?.message ?? "unknown"}. The API and the dashboard are ` +
        `built from the same schemas, so this is a version skew rather than a bad request.`,
    );
  }
  return parsed.data;
}

/** The same, for a mutation, with an input the shared request schema has already shaped. */
export async function mutate<T>(
  path: string,
  input: unknown,
  schema: ZodType<T>,
): Promise<T> {
  const raw: unknown = await transport.mutation(path, input);
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new ResponseShapeError(
      `${path} answered a shape this client does not recognise: ` +
        `${parsed.error.issues[0]?.message ?? "unknown"}.`,
    );
  }
  return parsed.data;
}

/**
 * The RFC 9457 body behind a failed call, or null.
 *
 * **Switch on `code`, never on the class** (c31b's lesson from the consuming side). The
 * server's `errorFormatter` puts the whole problem body at `error.data.problem`, so a
 * screen renders a refusal identically whichever transport delivered it — and a code the
 * screen does not know about falls through to the body's own `detail`, which is a
 * sentence somebody wrote for a person to read rather than a status number.
 *
 * Returns null for the errors that are not refusals: a dropped connection, a parse
 * failure, a bug. Those are not the server declining — they have no `code`, and treating
 * them as though they did would put a made-up vocabulary on screen.
 */
export function problemOf(error: unknown): ProblemBody | null {
  if (!(error instanceof TRPCClientError)) return null;
  const data: unknown = error.data;
  if (typeof data !== "object" || data === null || !("problem" in data)) return null;
  const problem = (data as { problem: unknown }).problem;
  if (typeof problem !== "object" || problem === null || !("code" in problem))
    return null;
  return problem as ProblemBody;
}

/** True when the failure is "you are not signed in" — the one code the shell routes on. */
export const isUnauthenticated = (error: unknown): boolean =>
  problemOf(error)?.code === "UNAUTHENTICATED";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A refused request is an answer, not a fault: retrying a 403 or a 409 asks the
      // same question again and gets the same refusal, three times, while the user waits.
      // Only genuine transport failures are worth another go.
      retry: (failureCount, error) => problemOf(error) === null && failureCount < 2,
      staleTime: 30_000,
    },
  },
});
